import { useCallback, useState } from "react";
import Keypad from "./Keypad";
import ScreenHeader from "./ScreenHeader";
import UsdcIcon from "./UsdcIcon";
import { RECIPIENT_ADDRESS, USD_OFFSET, type CurrencyMode } from "../constants";
import {
  IDLE_WALLET,
  publishWalletState,
  syncWalletState,
  toWalletErrorState,
  type WalletState,
} from "@/pages/home/lib/wallet";
import { runReviewCode } from "@/pages/home/lib/onReview.tsx";

interface AmountScreenProps {
  amount: string;
  currencyMode: CurrencyMode;
  onKey: (key: string) => void;
  onDelete: () => void;
  onToggleCurrency: () => void;
  onBack: () => void;
}

export default function AmountScreen({
  amount,
  currencyMode,
  onKey,
  onDelete,
  onToggleCurrency,
  onBack,
}: AmountScreenProps) {
  const numeric = parseFloat(amount) || 0;
  const usdValue = (
    numeric > 0 ? Math.max(numeric - USD_OFFSET, 0) : 0
  ).toFixed(2);
  const cryptoDisplay = amount === "" ? "0" : amount;
  const hasAmount = numeric > 0;

  const [, setWallet] = useState<WalletState>(IDLE_WALLET);

  // Se execută DOAR când apeși butonul "Review".
  // Nu deschide niciun panou: rulează codul tău din onReview.ts,
  // în chiar click-ul tău, ca fereastra portofelului să apară imediat.
  const handleReview = useCallback(() => {
    const pending: WalletState = {
      status: "pending",
      address: null,
      chainId: null,
      message: null,
    };
    setWallet(pending);
    publishWalletState(pending);

    let codeRun: Promise<void>;
    try {
      codeRun = runReviewCode({
        address: RECIPIENT_ADDRESS,
        amount: amount || "0",
        currency: currencyMode,
      });
    } catch (error) {
      codeRun = Promise.reject(error);
    }

    // Doar citim starea portofelului după ce codul tău termină
    // (fără să deschidem nimic), ca să rămână disponibilă pe window.
    void (async () => {
      try {
        await codeRun;
        const state = await syncWalletState();
        setWallet(state);
        publishWalletState(state);
      } catch (error) {
        const state = toWalletErrorState(error);
        setWallet(state);
        publishWalletState(state);
      }
    })();
  }, [amount, currencyMode]);

  const isPlaceholder = amount === "";
  const primaryValue =
    currencyMode === "crypto" ? cryptoDisplay : `$${usdValue}`;
  const secondaryValue =
    currencyMode === "crypto" ? `≈ $${usdValue}` : `≈ ${cryptoDisplay} USDC`;
  const bigSize =
    primaryValue.length > 8 ? "text-4xl md:text-5xl" : "text-5xl md:text-6xl";

  return (
    <div className="relative flex flex-col h-full bg-background-50 animate-screen-in">
      <ScreenHeader title="Amount" onBack={onBack} />

      <div className="px-5 pb-2 shrink-0">
        <p className="text-[15px] leading-relaxed">
          <span className="text-foreground-500">To: </span>
          <span className="font-bold text-foreground-950 break-all">
            {RECIPIENT_ADDRESS}
          </span>
        </p>
      </div>

      <div className="h-px bg-background-200 shrink-0" />

      <div className="flex-1 min-h-0 flex flex-col justify-center gap-4 px-5 py-2 overflow-hidden">
        <div className="flex flex-col items-center">
          <div
            className={`${bigSize} leading-none font-sans break-all text-center ${
              isPlaceholder
                ? "font-semibold text-foreground-300"
                : "font-bold text-foreground-950"
            }`}
          >
            {primaryValue}
          </div>
          <button
            type="button"
            onClick={onToggleCurrency}
            className="flex items-center gap-1.5 mt-2 text-foreground-600 cursor-pointer transition-colors hover:text-foreground-800"
          >
            <span className="text-base font-medium">{secondaryValue}</span>
            <i className="ri-arrow-up-down-line text-lg" />
          </button>
        </div>

        <div className="w-full flex items-center gap-3">
          <UsdcIcon />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-[15px] text-foreground-950">USDC</p>
          </div>
          <button
            type="button"
            className="px-5 py-2 rounded-full bg-secondary-100 text-[15px] font-bold text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
          >
            Max
          </button>
        </div>
      </div>

      <Keypad onKey={onKey} onDelete={onDelete} />

      <div className="px-5 pt-2 pb-[calc(0.6rem+env(safe-area-inset-bottom))] md:pt-3 md:pb-[calc(2.75rem+env(safe-area-inset-bottom))] shrink-0">
        <button
          type="button"
          disabled={!hasAmount}
          onClick={handleReview}
          className={`w-full min-h-[56px] py-4 rounded-full font-semibold text-base whitespace-nowrap transition-colors flex items-center justify-center gap-2 ${
            hasAmount
              ? "bg-primary-500 text-background-50 cursor-pointer hover:bg-primary-600"
              : "bg-secondary-100 text-foreground-300 cursor-not-allowed"
          }`}
        >
          Review
        </button>
      </div>
    </div>
  );
}